import { useEffect, useMemo, useRef, useState } from "react";
import { db, queue, uuid, type Point, type Contact, type Product, type Visit, type LocalPhoto, type RemotePhoto } from "../db";
import { api, ApiError } from "../api";
import { matrixFor, groupOf, allStock, getPosition, compressImage } from "../lib";
import { useDictation } from "../dictate";
import { sync, refreshCounters } from "../sync";
import { Shell, type ScreenProps } from "./common";

type Line = { productId: string; qty: number };

const MAX_PHOTOS = 5;

export default function VisitForm({
  updateReady, back, state, user, pointId, visitId,
}: ScreenProps & { pointId: string; visitId?: string }) {
  const [point, setPoint] = useState<Point | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [matrix, setMatrix] = useState<Product[]>([]);
  const [brandName, setBrandName] = useState("");
  const [handout, setHandout] = useState<Product[]>([]);
  const [stock, setStock] = useState<Map<string, number>>(new Map());
  const [prevShelf, setPrevShelf] = useState<Set<string>>(new Set());

  const [id] = useState(visitId ?? uuid());
  // Дата визита ставится один раз при создании. При правке её нельзя обновлять
  // на «сейчас»: визит был вчера, а не в момент, когда к нему приложили фото.
  const [visitedAt, setVisitedAt] = useState(() => new Date().toISOString());
  const [summary, setSummary] = useState("");
  const [contactId, setContactId] = useState<string>("");
  const [agreement, setAgreement] = useState("");
  const [nextOn, setNextOn] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [shelf, setShelf] = useState<Map<string, boolean>>(new Map());
  const [posOk, setPosOk] = useState(false);
  const [shelfNote, setShelfNote] = useState("");
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [remote, setRemote] = useState<RemotePhoto[]>([]);
  const [geo, setGeo] = useState<{ lat: number; lon: number; acc: number } | null>(null);
  const [geoState, setGeoState] = useState<"busy" | "done" | "fail">("busy");
  const [mode, setMode] = useState<"all" | "gaps">("all");
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Актуальный текст резюме для сохранения: state успевает обновиться позже,
  // чем сработает кнопка, если последнюю фразу договорили в микрофон.
  const summaryRef = useRef(summary);
  useEffect(() => { summaryRef.current = summary; }, [summary]);

  // Диктовка: распознанное дописывается в конец поля, а не затирает его.
  // Так можно говорить и править руками вперемешку, ничего не потеряв.
  const voice = useDictation((chunk) =>
    setSummary((s) => {
      const t = chunk.trim();
      if (!t) return s;
      if (!s.trim()) return t.charAt(0).toUpperCase() + t.slice(1);
      return s.replace(/\s+$/, "") + " " + t;
    })
  );

  useEffect(() => {
    void (async () => {
      const p = await db.points.get(pointId);
      if (!p) return;
      setPoint(p);
      setContacts((await db.contacts.where("pointId").equals(pointId).toArray()).filter((c) => !c.deletedAt));

      // Чек-лист — по бренду сотрудника: Лола проверяет Medipharma, Ульяна — Lacalut.
      // У руководителя бренда нет — ему по умолчанию Lacalut, как было всегда.
      // У уже записанного визита бренд берём из него самого: иначе руководитель,
      // дополнив визит Лолы, сохранил бы поверх её выкладки Medipharma пустой Lacalut.
      let brandId = user.brandId ?? null;
      if (visitId) {
        const firstCheck = (await db.visits.get(visitId))?.shelfChecks[0];
        if (firstCheck) brandId = (await db.products.get(firstCheck.productId))?.brandId ?? brandId;
      }
      const brands = await db.brands.toArray();
      const brand = brands.find((b) => b.id === brandId)
        ?? brands.find((b) => b.name === "Lacalut") ?? brands[0];
      if (brand) setBrandName(brand.name);
      if (p.type === "pharmacy" && brand) setMatrix(await matrixFor(p, brand.id));

      const st = await allStock(user.id);
      setStock(st);
      const prods = await db.products.toArray();
      setHandout(prods.filter((x) => x.active && (st.get(x.id) ?? 0) > 0)
        .sort((a, b) => a.sortOrder - b.sortOrder));

      // Что стояло на полке в прошлый раз — основа режима «чего не было».
      const past = (await db.visits.where("pointId").equals(pointId).toArray())
        .filter((v) => !v.deletedAt && v.id !== id)
        .sort((a, b) => b.visitedAt.localeCompare(a.visitedAt))[0];
      if (past) setPrevShelf(new Set(past.shelfChecks.filter((s) => s.onShelf).map((s) => s.productId)));

      if (visitId) {
        const v = await db.visits.get(visitId);
        if (v) {
          setVisitedAt(v.visitedAt);
          setRemote(v.photos ?? []);
          setSummary(v.summary); setContactId(v.contactId ?? "");
          setAgreement(v.agreement ?? ""); setNextOn(v.nextVisitOn ? v.nextVisitOn.slice(0, 10) : "");
          setLines(v.items); setPosOk(Boolean(v.posOk)); setShelfNote(v.shelfNote ?? "");
          setShelf(new Map(v.shelfChecks.map((s) => [s.productId, s.onShelf])));
          if (v.lat != null && v.lon != null) {
            setGeo({ lat: v.lat, lon: v.lon, acc: v.geoAccuracy ?? 0 });
            setGeoState("done");
          } else setGeoState("fail");
        }
        setPhotos(await db.photos.where("visitId").equals(visitId).toArray());
      } else {
        const pos = await getPosition();
        if (pos) {
          setGeo({ lat: pos.coords.latitude, lon: pos.coords.longitude, acc: Math.round(pos.coords.accuracy) });
          setGeoState("done");
        } else setGeoState("fail");
      }
    })();
  }, [pointId, visitId, user.id, user.brandId, id]);

  const groups = useMemo(() => {
    const visible = mode === "gaps" ? matrix.filter((p) => !prevShelf.has(p.id)) : matrix;
    const out: { name: string; items: Product[] }[] = [];
    for (const p of visible) {
      const g = groupOf(p);
      let e = out.find((x) => x.name === g);
      if (!e) { e = { name: g, items: [] }; out.push(e); }
      e.items.push(p);
    }
    return out;
  }, [matrix, mode, prevShelf]);

  const onShelfCount = matrix.filter((p) => shelf.get(p.id)).length;
  const gapsCount = matrix.filter((p) => !prevShelf.has(p.id)).length;

  // Ссылку на картинку надо создать один раз и освободить при уходе с экрана,
  // иначе каждая перерисовка формы оставляет за собой лишний объект в памяти.
  const photoUrls = useMemo(
    () => new Map(photos.map((p) => [p.id, URL.createObjectURL(p.blob)])),
    [photos]
  );
  useEffect(() => () => { photoUrls.forEach((u) => URL.revokeObjectURL(u)); }, [photoUrls]);

  // Снимки, сделанные на другом устройстве: на сервере они есть, а файлов здесь нет.
  // Считаем их в общий предел, иначе с телефона можно доложить ещё пять сверх тех,
  // что уже сняли планшетом.
  const elsewhere = remote.filter((r) => !photos.some((p) => p.id === r.id)).length;
  const totalPhotos = photos.length + elsewhere;

  /** Принимает и снимок с камеры, и файлы из галереи — в том числе сразу несколько. */
  async function addPhotos(files: FileList | null) {
    if (!files?.length) return;
    const room = MAX_PHOTOS - totalPhotos;
    if (room <= 0) return setError(`Больше ${MAX_PHOTOS} фото на визит не нужно`);

    const take = [...files].slice(0, room);
    let failed = 0;
    for (const file of take) {
      try {
        const blob = await compressImage(file);
        const row: LocalPhoto = { id: uuid(), visitId: id, blob, at: new Date().toISOString(), uploaded: 0 };
        await db.photos.put(row);
        setPhotos((ps) => [...ps, row]);
      } catch {
        // Чаще всего это HEIC с айфона — браузер такой формат не открывает.
        failed++;
      }
    }
    void refreshCounters();

    if (failed) setError(`Не удалось открыть ${failed} из ${take.length}. Если снимали айфоном, перешлите фото через Telegram — он переводит их в обычный формат`);
    else if (take.length < files.length) setError(`Взял ${take.length}: больше ${MAX_PHOTOS} фото на визит не нужно`);
    else setError(null);
  }

  async function removePhoto(pid: string) {
    // Уехавшее фото надо убрать и с сервера, иначе оно пропадёт только здесь,
    // а у руководителя останется. Без связи это сделать нечем — так и говорим.
    if (photos.find((p) => p.id === pid)?.uploaded === 1) {
      try {
        await api(`/api/photos/${pid}`, { method: "DELETE" });
      } catch (e) {
        // 404 — на сервере его и так нет, значит удалять больше нечего.
        if (!(e instanceof ApiError && e.status === 404)) {
          return setError("Это фото уже на сервере — удалить его получится только при связи");
        }
      }
    }
    await db.photos.delete(pid);
    setPhotos((ps) => ps.filter((p) => p.id !== pid));
    void refreshCounters();
  }

  async function save() {
    // Микрофон мог не успеть отдать последнюю фразу. Гасим его и ждём её,
    // иначе последнее сказанное молча не попадёт в визит.
    if (voice.listening) {
      voice.stop();
      await new Promise((r) => setTimeout(r, 400));
    }
    const text = summaryRef.current.trim();
    if (!text) return setError("Напиши хотя бы пару слов о визите");
    setBusy(true); setError(null);
    try {
      const row: Visit = {
        id, pointId, visitedAt, summary: text,
        contactId: contactId || null,
        lat: geo?.lat ?? null, lon: geo?.lon ?? null, geoAccuracy: geo?.acc ?? null,
        agreement: agreement.trim() || null,
        nextVisitOn: nextOn ? new Date(nextOn + "T09:00:00").toISOString() : null,
        posOk: point?.type === "pharmacy" ? posOk : null,
        shelfNote: shelfNote.trim() || null,
        items: lines.filter((l) => l.qty > 0),
        shelfChecks: matrix.map((p) => ({ productId: p.id, onShelf: Boolean(shelf.get(p.id)) })),
        updatedAt: new Date().toISOString(),
      };
      await db.visits.put(row);
      await queue("visit", id);
      void sync();
      back();
    } catch {
      setError("Не удалось сохранить визит");
      setBusy(false);
    }
  }

  if (!point) {
    return <Shell title="Визит" onBack={back} state={state} updateReady={updateReady}><div className="empty">Загрузка…</div></Shell>;
  }

  return (
    <Shell
      title={(visitId ? "Правка визита · " : "Визит · ") + point.name}
      onBack={back}
      state={state} updateReady={updateReady}
      footer={
        <>
          <button className="btn primary" onClick={save} disabled={busy}>
            {busy ? "Сохраняем…" : "Сохранить визит"}
          </button>
          <button className="btn ghost" onClick={back}>Отмена</button>
        </>
      }
    >
      {error && <div className="err">{error}</div>}

      <div className={"geo" + (geoState === "done" ? "" : " pending")}>
        <span aria-hidden="true">📍</span>
        {geoState === "busy" && "Определяем, где вы…"}
        {geoState === "done" && geo && `Геометка есть · точность ${geo.acc} м`}
        {geoState === "fail" && "Геометку получить не удалось — визит всё равно сохранится"}
      </div>

      <div className="field">
        <label className="label" htmlFor="ct">Контактное лицо</label>
        <select id="ct" className="inp" value={contactId} onChange={(e) => setContactId(e.target.value)}>
          <option value="">— не указан —</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.fullName} — {c.position}</option>)}
        </select>
      </div>

      <div className="field">
        <div className="labelrow">
          <label className="label" htmlFor="sm">Резюме визита <span className="req">*</span></label>
          {voice.supported && (
            <button type="button" className={"mic" + (voice.listening ? " on" : "")}
              aria-pressed={voice.listening}
              onClick={() => (voice.listening ? voice.stop() : voice.start())}>
              <span aria-hidden="true">{voice.listening ? "■" : "🎤"}</span>
              {voice.listening ? "Стоп" : "Диктовать"}
            </button>
          )}
        </div>
        <textarea id="sm" className="inp" value={summary}
          placeholder="Что обсудили, что решили"
          onChange={(e) => setSummary(e.target.value)} />
        {voice.listening && (
          <div className="interim">{voice.interim || "Слушаю…"}</div>
        )}
        {voice.error && <div className="hint bad">{voice.error}</div>}
        <div className="hint">
          {voice.supported
            ? "Говори обычным голосом, текст появится сам. Без интернета диктуй микрофоном на клавиатуре. "
            : "Диктовать удобнее — нажми микрофон на клавиатуре планшета. "}
          {/* Открываем отдельной вкладкой: уходить с наполовину заполненного визита нельзя. */}
          <a href="/pamyatka/golos" target="_blank" rel="noreferrer">Как диктовать</a>
        </div>
      </div>

      {/* --- что оставил --- */}
      <div className="block">
        <div className="blockhead"><span className="blocktitle">Что оставил</span></div>
        {handout.length === 0 && (
          <div className="hint">На руках ничего не числится — попроси выдачу у руководителя.</div>
        )}
        {lines.map((l, idx) => {
          const left = stock.get(l.productId) ?? 0;
          const over = l.qty > left;
          return (
            <div key={idx}>
              <div className="itemrow">
                <select className="inp" value={l.productId}
                  onChange={(e) => setLines((ls) =>
                    ls.map((x, i) => (i === idx ? { ...x, productId: e.target.value } : x)))}>
                  {handout.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input className="inp" type="number" min={0} inputMode="numeric" value={l.qty}
                  onChange={(e) => setLines((ls) =>
                    ls.map((x, i) => (i === idx ? { ...x, qty: Number(e.target.value) || 0 } : x)))} />
                <button className="iconbtn" title="Убрать"
                  onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}>×</button>
              </div>
              <div className={"stockline" + (over ? " bad" : "")}>
                {over ? `На руках только ${left} шт. — столько оставить нельзя`
                      : `У меня на руках: ${left} шт.`}
              </div>
            </div>
          );
        })}
        {handout.length > 0 && (
          <button className="btn tiny"
            onClick={() => setLines((ls) => [...ls, { productId: handout[0].id, qty: 1 }])}>
            + Добавить строку
          </button>
        )}
      </div>

      {/* --- выкладка: только аптеки --- */}
      {point.type === "pharmacy" && (
        <div className="block">
          <div className="blockhead">
            <span className="blocktitle">Выкладка{brandName ? " · " + brandName : ""}</span>
            <span className="only">только аптеки</span>
          </div>

          {matrix.length === 0 ? (
            <div className="hint">
              {brandName === "Lacalut"
                ? "Матрицы для этой точки нет. Укажи контрагента в карточке точки — и перечень подтянется сам."
                : `Для этой аптеки офис не задал перечень ${brandName}. Визит записывается и без него.`}
            </div>
          ) : (
            <>
              <div className="seg" style={{ marginBottom: 10 }}>
                <button type="button" aria-pressed={mode === "all"} onClick={() => setMode("all")}>
                  Весь список <span style={{ opacity: .6 }}>{matrix.length}</span>
                </button>
                <button type="button" aria-pressed={mode === "gaps"} onClick={() => setMode("gaps")}>
                  Чего не было <span style={{ opacity: .6 }}>{gapsCount}</span>
                </button>
              </div>
              <div className="hint" style={{ marginBottom: 6 }}>
                Отмечено на полке: {onShelfCount} из {matrix.length}
              </div>

              {groups.map((g) => {
                const isClosed = closed.has(g.name);
                const got = g.items.filter((p) => shelf.get(p.id)).length;
                return (
                  <div key={g.name}>
                    <button className="grp" onClick={() => setClosed((s) => {
                      const n = new Set(s); n.has(g.name) ? n.delete(g.name) : n.add(g.name); return n;
                    })}>
                      <span className="arw">{isClosed ? "▸" : "▾"}</span>
                      {g.name}
                      <span className="cnt">{got} из {g.items.length} на полке</span>
                    </button>
                    {!isClosed && g.items.map((p) => (
                      <label className="shelf" key={p.id}>
                        <input type="checkbox" checked={Boolean(shelf.get(p.id))}
                          onChange={(e) => setShelf((m) => new Map(m).set(p.id, e.target.checked))} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          {p.name}
                          {p.article && <span className="hint"> · арт. {p.article}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                );
              })}

              <label className="shelf" style={{ borderTop: "1px solid var(--border)", marginTop: 8 }}>
                <input type="checkbox" checked={posOk} onChange={(e) => setPosOk(e.target.checked)} />
                POS-материалы на месте
              </label>
              <div className="field" style={{ marginTop: 12, marginBottom: 0 }}>
                <label className="label" htmlFor="sn">Комментарий по выкладке</label>
                <input id="sn" className="inp" value={shelfNote}
                  placeholder="Например: Teens задвинут вниз"
                  onChange={(e) => setShelfNote(e.target.value)} />
              </div>
            </>
          )}
        </div>
      )}

      {/* --- фото --- */}
      <div className="block">
        <div className="blockhead">
          <span className="blocktitle">Фото</span>
          <span className="hint">до пяти</span>
        </div>
        {elsewhere > 0 && (
          // Файлы лежат на том устройстве, где сняты. Открыв визит с телефона,
          // сотрудник увидит пусто — и решит, что фото пропали.
          <div className="hint" style={{ marginBottom: 10 }}>
            Ещё {elsewhere} фото снято на другом устройстве. Они на сервере
            и никуда не денутся, просто здесь их не видно.
          </div>
        )}
        <div className="photos">
          {photos.map((p) => (
            <span className="photowrap" key={p.id}>
              <img className="photo" src={photoUrls.get(p.id)} alt="" />
              <button className="photodel" onClick={() => removePhoto(p.id)} title="Удалить">×</button>
            </span>
          ))}
          {totalPhotos < MAX_PHOTOS && (
            <>
              <label className="photoadd">
                <span aria-hidden="true" style={{ fontSize: 20 }}>📷</span>
                Снять
                <input type="file" accept="image/*" capture="environment" hidden
                  onChange={(e) => { void addPhotos(e.target.files); e.target.value = ""; }} />
              </label>
              {/* Планшетом в торговом зале снимать неудобно, поэтому фото часто
                  делают телефоном и переносят. Без выбора из галереи такой
                  снимок приложить нечем: атрибут capture открывает только камеру. */}
              <label className="photoadd">
                <span aria-hidden="true" style={{ fontSize: 20 }}>🖼️</span>
                Из галереи
                <input type="file" accept="image/*" multiple hidden
                  onChange={(e) => { void addPhotos(e.target.files); e.target.value = ""; }} />
              </label>
            </>
          )}
        </div>
      </div>

      <div className="field">
        <label className="label" htmlFor="ag">Договорились / что сделать к следующему разу</label>
        <input id="ag" className="inp" value={agreement}
          placeholder="Привезти воблер, проверить перестановку"
          onChange={(e) => setAgreement(e.target.value)} />
      </div>

      <div className="field">
        <label className="label" htmlFor="nx">Когда прийти снова</label>
        <input id="nx" className="inp" type="date" value={nextOn}
          onChange={(e) => setNextOn(e.target.value)} />
        <div className="hint">Можно не заполнять — посчитается по категории точки</div>
      </div>
    </Shell>
  );
}
